import { RadioBtnInventory } from '@inventory/models/radio-btn-inventory.model';
import { FoodCostReportFields, InventoryDetails } from '@usf/inventory-types';

export const transformFoodCostReport = (
  value: Map<string, any | any[]>,
): FoodCostReportFields => {
  const beginInventories = value.get('Begin Inventories');
  const endInventories = value.get('End Inventories');
  const salesRevenue = value.get('Sales Revenues');
  let foodPurchases = value.get('Food Purchases') ?? new Map<number, any>();
  foodPurchases.set(4, value.get('Purchase Total'));
  const startPeriod = value.get('Begin Inventory Date');
  const endPeriod = value.get('End Inventory Date');

  const openingInventory = calculateInventoryTotals(beginInventories);
  const closingInventory = calculateInventoryTotals(endInventories);
  const purchases = calculateTotals(foodPurchases);
  const foodSales = calculateTotals(salesRevenue);
  const foodCostTotal = calculateFoodCostTotal(
    openingInventory,
    closingInventory,
    purchases,
  );
  const foodCostPercentage = calculateFoodCostPercentage(
    foodCostTotal,
    foodSales,
  );
  const departementNumbers = parseDepartmentNumbers(
    getUniqueDepartments([...beginInventories, ...endInventories]),
  );

  return {
    departments: departementNumbers,
    startingInventory: parseInventoryDetails(beginInventories),
    endInventory: parseInventoryDetails(endInventories),
    startPeriod,
    endPeriod,
    usfProductsPurchaseAmount: foodPurchases.has(4)
      ? Number(foodPurchases.get(4))
      : 0,
    nonUsfProductsPurchaseAmount: foodPurchases.has(3)
      ? Number(foodPurchases.get(3))
      : 0,
    salesRevenue: foodSales,
    foodCostPercentage: foodCostPercentage,
    foodCostTotal,
  } as unknown as FoodCostReportFields;
};

export const calculateInventoryTotals = inventories => {
  if (typeof inventories) {
    const regex = /([\$\,])/g;
    return inventories.reduce(
      (acc, currentValue) =>
        acc + Number(currentValue.value.toString().replace(regex, '')),
      0,
    );
  }
  return 0;
};

export const calculateTotals = (purhaseMap: Map<number, any>) => {
  let totalPurchaseCost = 0;
  if (purhaseMap) {
    purhaseMap.forEach(value => {
      totalPurchaseCost += Number(value);
    });
  }
  return totalPurchaseCost;
};

export const calculateFoodCostPercentage = (
  foodCostTotal: number,
  foodSales: number,
) => {
  const percentage = Number(((foodCostTotal / foodSales) * 100).toFixed(1));
  return isFinite(percentage) && percentage > 0 ? percentage : 0;
};

export const parseInventoryDetails = (inventoryArray): InventoryDetails[] =>
  inventoryArray.map(inventory => ({
    inventoryId: Number(inventory.inventoryId),
    inventoryName: inventory.inventoryName,
    inventoryTotal: inventory.value ?? 0,
  }));

export const getUniqueDepartments = (
  inventories: RadioBtnInventory[],
): String[] => {
  if (inventories) {
    const deptNames = new Set<String>();
    inventories.forEach(inventory => {
      deptNames.add(inventory?.departmentName);
    });
    return Array.from(deptNames);
  }
  return [];
};

export const parseDepartmentNumbers = (departmentNames): Number[] => {
  return departmentNames.map(deptName => {
    if (!deptName.includes('(')) return;
    const startPos = deptName.indexOf('(') + 1;
    const endPos = deptName.indexOf(')') - 1;
    return Number(deptName.substring(startPos, endPos));
  });
};

export const calculateFoodCostTotal = (
  openingInventory,
  closingInventory,
  purchases,
) => Number((purchases + openingInventory - closingInventory).toFixed(2));
